import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { SERVER_URL, AUTH_STORAGE_KEY } from "../socket";
import {
  buildForwardSubject,
  buildReplySubject,
  collectReplyAllRecipients,
  extractEmailAddress,
  extractRecipientEmails,
  isValidEmail,
  plainTextToHtml,
  escapeHtml,
  parseEmailList,
} from "../utils/mailUtils";
import { buildSignatureHtml } from "../utils/signatureUtils";

function buildQuotedBody(mail, formatter) {
  if (!mail) return "";
  const sentAtValue = mail.sentAt || mail.date || null;
  const formattedDate = sentAtValue ? new Date(sentAtValue).toLocaleString() : "";
  const headerPieces = [];
  if (formattedDate) headerPieces.push(`On ${formattedDate}`);
  if (mail.from) headerPieces.push(mail.from);
  const headerText = headerPieces.length ? `${headerPieces.join(" ")} wrote:` : "Previous message:";

  let quotedSource = "<em>(No content)</em>";
  if (mail.body && typeof mail.body === "string" && mail.body.trim().length) {
    quotedSource = mail.body;
  } else if (mail.bodyPlain && typeof mail.bodyPlain === "string" && mail.bodyPlain.trim().length) {
    quotedSource = formatter(mail.bodyPlain.trim());
  }

  return `<p>${escapeHtml(headerText)}</p><blockquote>${quotedSource}</blockquote>`;
}

export default function useCompose({
  authHeaders,
  signatureList,
  defaultSignatureId,
  signatureEditable = true,
  updateToast,
}) {
  const editorRef = useRef(null);
  const [composeForm, setComposeForm] = useState({
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  body: "",
  mailStatus: "NA",
});
  // const [composeForm, setComposeForm] = useState({ to: "", cc: "", bcc: "", subject: "", body: "" });
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [composeDraftId, setComposeDraftId] = useState(null);
  const [composeSignatureId, setComposeSignatureId] = useState(defaultSignatureId);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  const signatureRecord = useMemo(() => {
    if (!signatureList.length) return null;
    const chosen = signatureList.find((entry) => String(entry.id) === String(composeSignatureId));
    if (chosen) return chosen;
    const fallback = signatureList.find((entry) => String(entry.id) === String(defaultSignatureId));
    if (fallback) return fallback;
    return signatureList[0];
  }, [composeSignatureId, defaultSignatureId, signatureList]);

  const previousSignatureRef = useRef(signatureRecord?.content || "");

  const signatureHtml = useMemo(
    () => buildSignatureHtml(signatureRecord?.content || ""),
    [signatureRecord?.content]
  );

  useEffect(() => {
    if (!signatureList.length) return;
    setComposeSignatureId((prev) => {
      const match = signatureList.find((entry) => String(entry.id) === String(prev));
      if (match) return match.id;
      const fallback = signatureList.find((entry) => String(entry.id) === String(defaultSignatureId));
      if (fallback) return fallback.id;
      return signatureList[0].id;
    });
  }, [defaultSignatureId, signatureList]);

  const createComposeBody = useCallback(
    (followingHtml = "") => {
      const spacer = "<p><br></p>";
      let body = spacer;
      // include signature HTML inside the editable body only when signatures are editable
      if (signatureEditable && signatureHtml) {
        body += signatureHtml;
      }
      if (followingHtml) {
        body += followingHtml;
      }
      return body;
    },
    [signatureHtml]
  );

  const createFinalBody = useCallback((bodyHtml = "") => {
    // When signatures are non-editable, append signatureHtml when sending
    if (!signatureEditable && signatureHtml) {
      return (bodyHtml || "") + signatureHtml;
    }
    return bodyHtml || "";
  }, [signatureEditable, signatureHtml]);

  const savedRangeRef = useRef(null);

  const focusEditorStart = useCallback(() => {
    if (typeof window === "undefined") return;
    const node = editorRef.current;
    if (!node) return;
    node.focus();
    const selection = window.getSelection();
    if (!selection) return;
    try {
      selection.removeAllRanges();
      const range = document.createRange();
      if (node.firstChild) {
        range.setStart(node.firstChild, 0);
      } else {
        range.setStart(node, 0);
      }
      range.collapse(true);
      selection.addRange(range);
      // save for future toolbar actions
      savedRangeRef.current = range.cloneRange();
    } catch {}
  }, []);

  const restoreSavedSelection = useCallback(() => {
    if (typeof window === "undefined") return false;
    const node = editorRef.current;
    if (!node) return false;
    const selection = window.getSelection();
    if (!selection) return false;

    const saved = savedRangeRef.current;
    if (saved) {
      try {
        selection.removeAllRanges();
        selection.addRange(saved.cloneRange());
        // ensure editor receives focus
        if (node.focus) node.focus();
        return true;
      } catch (err) {
        // fall through
      }
    }
    return false;
  }, []);

  // Listen for selection changes to save the current range when inside editor
  useEffect(() => {
    const handler = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const node = editorRef.current;
        if (!node) return;
        // if selection is inside editor, save it
        if (range && node.contains(range.startContainer)) {
          savedRangeRef.current = range.cloneRange();
        }
      } catch (err) {
        // ignore
      }
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  const resetComposeForm = useCallback(() => {
    const defaultBody = createComposeBody("");
    setComposeForm({
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  body: defaultBody,
  replyToId: null,
});

    // setComposeForm({ to: "", cc: "", bcc: "", subject: "", body: defaultBody });
    setComposeAttachments([]);
    setComposeSignatureId(defaultSignatureId);
    setComposeDraftId(null);
    if (editorRef.current) {
      editorRef.current.innerHTML = defaultBody;
      focusEditorStart();
    }
  }, [createComposeBody, defaultSignatureId, focusEditorStart]);

 const handleReply = useCallback(
  (mail) => {
    if (!mail) {
      updateToast?.("Cannot reply: mail data missing.");
      return null;
    }

    // 🔒 HARD NORMALIZATION — THIS IS THE KEY FIX
    let senderEmail = "";

    if (typeof mail.from === "string") {
      senderEmail = extractEmailAddress(mail.from);
    } else if (mail.from?.address) {
      senderEmail = extractEmailAddress(mail.from.address);
    } else if (mail.fromEmail) {
      senderEmail = extractEmailAddress(mail.fromEmail);
    }

    if (!senderEmail || !isValidEmail(senderEmail)) {
      console.error("Reply failed — invalid sender:", mail);
      updateToast?.("Cannot reply: sender email unavailable.");
      return null;
    }

    const replySubject = buildReplySubject(mail.subject || "");
    const replyBody = buildQuotedBody(mail, plainTextToHtml);
    const composedBody = createComposeBody(replyBody);

    setComposeSignatureId(defaultSignatureId);
    setComposeForm({
      to: [senderEmail], // ✅ ALWAYS ARRAY
      cc: [],
      bcc: [],
      subject: replySubject,
      body: composedBody,
      replyToId: mail.id || null,
    });

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = composedBody;
        focusEditorStart();
      }
    }, 0);

    return {
      to: senderEmail,
      subject: replySubject,
      body: composedBody,
    };
  },
  [createComposeBody, defaultSignatureId, focusEditorStart, updateToast]
);

 const handleReplyAll = useCallback(
  (mail) => {
    if (!mail) {
      updateToast?.("Cannot reply all: mail data missing.");
      return null;
    }
    let myEmail = "";
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      myEmail = extractEmailAddress(parsed?.user?.email || parsed?.email || "");
    } catch (e) {
      myEmail = "";
    }

    const { to: replyToRecipients, cc: replyToCC, bcc: replyToBCC } = collectReplyAllRecipients(mail, myEmail);

    const replySubject = buildReplySubject(mail.subject || "");
    const replyBody = buildQuotedBody(mail, plainTextToHtml);
    const composedBody = createComposeBody(replyBody);

    setComposeSignatureId(defaultSignatureId);
    setComposeForm({
      to: replyToRecipients,
      cc: replyToCC,
      bcc: replyToBCC,
      subject: replySubject,
      body: composedBody,
      replyToId: mail.id || null,
      mailStatus: mail.mailStatus || "NA",
    });

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = composedBody;
        focusEditorStart();
      }
    }, 0);

    return {
      to: replyToRecipients,
      cc: replyToCC,
      subject: replySubject,
      body: composedBody,
    };
  },
  [createComposeBody, defaultSignatureId, focusEditorStart, updateToast]
);

  const handleForward = useCallback(
    (mail) => {
      if (!mail) return null;
      const forwardSubject = buildForwardSubject(mail.subject || "");
      const replyBody = buildQuotedBody(mail, plainTextToHtml);
      const composedBody = createComposeBody(replyBody);

      const mailAttachments = Array.isArray(mail.attachments) && mail.attachments.length
        ? mail.attachments
            .filter((item) => item && typeof item === "object" && item.url)
            .map((item) => ({
              url: item.url,
              name: item.name || item.filename || undefined,
              mimeType: item.mimeType || item.contentType || undefined,
              size: item.size || undefined,
            }))
        : mail.attachmentUrl
        ? [
            {
              url: mail.attachmentUrl,
              name: mail.attachmentName || undefined,
              mimeType: mail.attachmentMime || undefined,
              size: mail.attachmentSize || undefined,
            },
          ]
        : [];

      // Only set mail attachments if there are no user-added attachments
      if (!composeAttachments.length) {
        setComposeAttachments(mailAttachments);
      }
      
      setComposeSignatureId(defaultSignatureId);
      setComposeForm({ to: [], cc: [], bcc: [], subject: forwardSubject, body: composedBody });
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = composedBody || "";
          focusEditorStart();
        }
      }, 0);

      return {
        subject: forwardSubject,
        body: composedBody,
        attachments: composeAttachments.length ? composeAttachments : mailAttachments,
      };
    },
    [createComposeBody, defaultSignatureId, focusEditorStart, composeAttachments.length]
  );

  useEffect(() => {
    const previousSignature = previousSignatureRef.current || "";
    const currentSignature = signatureRecord?.content || "";
    if (previousSignature === currentSignature) return;

    const previousSignatureHtml = buildSignatureHtml(previousSignature);
    const previousDefaultBody = `<p><br></p>${previousSignatureHtml || ""}`;
    const nextDefaultBody = createComposeBody("");

    if (!composeForm.body || composeForm.body === previousDefaultBody) {
      setComposeForm((prev) => ({ ...prev, body: nextDefaultBody }));
      if (editorRef.current) {
        editorRef.current.innerHTML = nextDefaultBody;
        focusEditorStart();
      }
    }

    previousSignatureRef.current = currentSignature;
  }, [composeForm.body, createComposeBody, focusEditorStart, signatureRecord?.content]);

  const uploadSelectedFiles = useCallback(
    async (files) => {
      const fileList = Array.from(files || []);
      if (!fileList.length) return;

      if (!authHeaders) {
        updateToast?.("Authentication required to add attachments.");
        return;
      }

      setUploadingAttachment(true);
      let uploadedAny = false;

      try {
        for (const file of fileList) {

          const formData = new FormData();
          formData.append("file", file);

          const res = await axios.post(`${SERVER_URL}/api/mail/attachments`, formData, {
            headers: {
              ...authHeaders,
              "Content-Type": "multipart/form-data",
            },
          });

          if (res.data?.url) {
            setComposeAttachments((prev) => [
              ...prev,
              {
                url: res.data.url,
                name: file.name,
                mimeType: res.data?.mimeType || file.type,
                size: res.data?.size || file.size,
              },
            ]);
            uploadedAny = true;
          }
        }

        if (uploadedAny) {
          updateToast?.(fileList.length > 1 ? "Attachments added." : "Attachment added.");
        }
      } catch (err) {
        const message = err.response?.data?.error || "Failed to upload attachment.";
        updateToast?.(message);
      } finally {
        setUploadingAttachment(false);
      }
    },
    [authHeaders, updateToast]
  );

  const handleAttachmentSelect = useCallback(
    async (event) => {
      const files = event.target?.files;
      if (!files || !files.length) return;
      await uploadSelectedFiles(files);
      event.target.value = "";
    },
    [uploadSelectedFiles]
  );

    const handleEditDraft = useCallback((mail) => {
      if (!mail) return null;
      // mail.to / cc / bcc may be strings like "a@x.com, b@x.com"
      const toList = Array.isArray(mail.to) ? mail.to : (typeof mail.to === 'string' ? mail.to.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean) : []);
      const ccList = Array.isArray(mail.cc) ? mail.cc : (typeof mail.cc === 'string' ? mail.cc.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean) : []);
      const bccList = Array.isArray(mail.bcc) ? mail.bcc : (typeof mail.bcc === 'string' ? mail.bcc.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean) : []);

      const mailAttachments = Array.isArray(mail.attachments) && mail.attachments.length
        ? mail.attachments.map((item) => ({ url: item.url, name: item.name || item.filename || undefined, mimeType: item.mimeType || item.contentType || undefined, size: item.size || undefined }))
        : mail.attachmentUrl ? [{ url: mail.attachmentUrl, name: mail.attachmentName || undefined, mimeType: mail.attachmentMime || undefined, size: mail.attachmentSize || undefined }] : [];

      setComposeAttachments(mailAttachments);
      setComposeSignatureId(defaultSignatureId);
      setComposeDraftId(mail.id || null);

      const body = mail.body || mail.bodyPlain || "";
      const composedBody = createComposeBody(body);

      setComposeForm({ to: toList, cc: ccList, bcc: bccList, subject: mail.subject || "", body: composedBody, mailStatus: mail.mailStatus || "NA" });

      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = composedBody || "";
          focusEditorStart();
        }
      }, 0);

      return true;
    }, [createComposeBody, defaultSignatureId, focusEditorStart]);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    const items = Array.from(event.dataTransfer?.items || []);
    const hasFiles = items.some((item) => item.kind === "file");
    if (!hasFiles) return;
    dragCounterRef.current += 1;
    setIsDragActive(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    if (dragCounterRef.current > 0) {
      dragCounterRef.current -= 1;
    }
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (event) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files || []);
      dragCounterRef.current = 0;
      setIsDragActive(false);
      if (!files.length) return;
      await uploadSelectedFiles(files);
    },
    [uploadSelectedFiles]
  );

  return {
    editorRef,
    composeForm,
    setComposeForm,
    composeAttachments,
    setComposeAttachments,
    composeSignatureId,
    setComposeSignatureId,
    uploadingAttachment,
    setUploadingAttachment,
    isDragActive,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleAttachmentSelect,
    uploadSelectedFiles,
    createComposeBody,
    createFinalBody,
    focusEditorStart,
    restoreSavedSelection,
    resetComposeForm,
    handleReply,
    handleReplyAll,
    handleForward,
    handleEditDraft,
    signatureHtml,
    signatureEditable,
  };
}
